// ARCHIVO: src/utils/loggers.js
// UBICACIÓN: C:\xampp\htdocs\react-migration\src\utils\loggers.js
// DESCRIPCIÓN: Sistema de logging para React

import { useState, useEffect, useCallback } from 'react';

// Hook personalizado para usar el logger
export const useLogger = () => {
  const [logs, setLogs] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Inicializar logs desde localStorage
  useEffect(() => {
    try {
      const savedLogs = localStorage.getItem('react_logs');
      if (savedLogs) {
        const parsedLogs = JSON.parse(savedLogs);
        setLogs(parsedLogs);
        console.log('📥 Cargados', parsedLogs.length, 'logs desde localStorage');
      }
      setIsInitialized(true);
    } catch (error) {
      console.error('Error cargando logs:', error);
      setIsInitialized(true);
    }
  }, []);

  // Función para agregar logs
  const log = useCallback((message, level = 'info', data = null) => {
    const timestamp = new Date().toISOString();
    const logEntry = {
      id: Date.now() + Math.random(),
      timestamp,
      message,
      level,
      data
    };

    setLogs(prevLogs => {
      const newLogs = [logEntry, ...prevLogs].slice(0, 1000); // Mantener solo los últimos 1000
      try {
        localStorage.setItem('react_logs', JSON.stringify(newLogs));
      } catch (error) {
        console.error('Error guardando logs:', error);
      }
      return newLogs;
    });

    // Mostrar en consola con colores
    const colors = {
      info: 'color: #3B82F6',
      success: 'color: #10B981',
      warning: 'color: #F59E0B',
      error: 'color: #EF4444',
      debug: 'color: #8B5CF6'
    };

    const color = colors[level] || colors.info;
    console.log(`%c${message}`, color, data || '');
  }, []);

  // Función para limpiar logs
  const clearLogs = useCallback(() => {
    setLogs([]);
    localStorage.removeItem('react_logs');
    console.log('�� Logs limpiados');
  }, []);

  // Función para descargar logs
  const downloadLogs = useCallback(() => {
    try {
      const logText = logs.map(log => 
        `[${log.timestamp}] ${log.level.toUpperCase()}: ${log.message}`
      ).join('\n');
      
      const blob = new Blob([logText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `react-logs-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      console.log('📥 Logs descargados');
    } catch (error) {
      console.error('Error descargando logs:', error);
    }
  }, [logs]);

  // Función para obtener logs filtrados
  const getLogs = useCallback((level = null) => {
    if (level) {
      return logs.filter(log => log.level === level);
    }
    return logs;
  }, [logs]);

  return {
    log,
    logs,
    clearLogs,
    downloadLogs,
    getLogs,
    isInitialized
  };
};

// Logger estático para uso fuera de componentes
class Logger {
  constructor() {
    this.logs = [];
    this.loadFromStorage();
  }

  loadFromStorage() {
    try {
      const savedLogs = localStorage.getItem('react_logs');
      if (savedLogs) {
        this.logs = JSON.parse(savedLogs);
      }
    } catch (error) {
      console.error('Error cargando logs:', error);
    }
  }

  log(message, level = 'info', data = null) {
    const timestamp = new Date().toISOString();
    const logEntry = {
      id: Date.now() + Math.random(),
      timestamp,
      message,
      level,
      data
    };

    this.logs.unshift(logEntry);
    this.logs = this.logs.slice(0, 1000); // Mantener solo los últimos 1000

    try {
      localStorage.setItem('react_logs', JSON.stringify(this.logs));
    } catch (error) {
      console.error('Error guardando logs:', error);
    }

    // Mostrar en consola con colores
    const colors = {
      info: 'color: #3B82F6',
      success: 'color: #10B981',
      warning: 'color: #F59E0B',
      error: 'color: #EF4444',
      debug: 'color: #8B5CF6'
    };

    const color = colors[level] || colors.info;
    console.log(`%c${message}`, color, data || '');
  }

  info(message, data = null) {
    this.log(message, 'info', data);
  }

  success(message, data = null) {
    this.log(message, 'success', data);
  }

  warning(message, data = null) {
    this.log(message, 'warning', data);
  }

  error(message, data = null) {
    this.log(message, 'error', data);
  }

  debug(message, data = null) {
    this.log(message, 'debug', data);
  }

  clear() {
    this.logs = [];
    localStorage.removeItem('react_logs');
    console.log('�� Logs limpiados');
  }

  download() {
    try {
      const logText = this.logs.map(log => 
        `[${log.timestamp}] ${log.level.toUpperCase()}: ${log.message}`
      ).join('\n');
      
      const blob = new Blob([logText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `react-logs-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      console.log('📥 Logs descargados');
    } catch (error) {
      console.error('Error descargando logs:', error);
    }
  }

  getLogs(level = null) {
    if (level) {
      return this.logs.filter(log => log.level === level);
    }
    return this.logs;
  }
}

// Instancia global del logger
export const logger = new Logger();

// Exportar funciones individuales para compatibilidad
export const { info, success, warning, error, debug, clear, download, getLogs } = logger;